import { resolveComplexTemplate } from "../../utils/template/resolveComplexTemplate.js";
import { enhanceHTML } from "../../utils/template/enhanceHTML.js";
import { generateTable } from "../../ui/table/Utils.js";
import * as templateFunctions from "./templateFunctions/index.js";
import * as templates from "./templates/index.js";

export { PopupRenderer }

/**
 * Static class for resolving popup templates depending on the type and
 * rendering the content.
 *
 * @author rhess <robin.hess@awi.de>
 * 
 * @memberof vef.map.popup
 */
class PopupRenderer {

    static templates = {};
    static templatesFiltered = {};
    static urlCache = {};
    static templateUrlPrefix = "";

    static registerTemplate(name, template) {
        PopupRenderer.templates[name] = template;
    }

    static registerFilteredTemplate(regex, template) {
        PopupRenderer.templatesFiltered[regex] = template;
    }

    /**
     * Builds and returns an array of features and HTML strings describing
     * the features defined in content.
     * 
     * Returns an object like
     * ```
     * {
     *   response: response,
     *   feature: feature,
     *   layer: layer,
     *   html: html
     * }
     * ```
     *
     * @param {object} content
     * @param {object} response
     * @param {object} layer
     * @param {string} style "popup" or "sidebar"
     * @param {object} customFunctions
     */
    static async render(content, response, layer, style, customFunctions) {
        const uId = layer.uniqueId;

        // simple handling of unparsed responses (e.g. html)
        if ((typeof content == "string") || (content instanceof HTMLElement)) {
            return PopupRenderer.renderHtml(content, response, layer);
        }

        // handle invalid content
        if (!layer) {
            console.warn('Layer was not defined for the popup content');
            return [];
        } else if (!content || (typeof content != "object")) {
            console.warn('Empty or error content for ', uId);
            return [];

        } else if (!["FeatureCollection", "Feature"].includes(content?.type)) {
            console.warn('The popup content cannot be parsed as a GeoJson FeatureCollection');
            return [];
        } else if (content.ServiceExceptionReport) {
            console.warn('Exception report for ', uId, content.ServiceExceptionReport);
            return [];
        }

        if (content.type == "Feature") content = {
            type: "FeatureCollection",
            features: [content]
        }

        if (!("features" in content)) {
            console.warn('"features" is missing in the GeoJson FeatureCollection');
            return [];
        }

        // resolve and replace templates
        const template = await PopupRenderer.getTemplate(layer, style);
        if (template) {
            return PopupRenderer.renderTemplate(content, response, layer, style, customFunctions, template);
        } else {
            const features = (!content.features) ? [content] : content.features;
            return PopupRenderer.renderJsonFeatureTable(features, response, layer, style);
        }
    }

    static renderHtml(content, response, layer) {
        return [{
            response: response,
            feature: content,
            layer: layer,
            html: content
        }];
    }

    static renderJsonFeatureTable(features, response, layer, style) {
        const responses = [];
        for (let i = 0; i < features.length; ++i) {
            if (features[i].properties) {
                const properties = features[i].properties;
                let html = generateTable((style != "sidebar") ? PopupRenderer.reduceProperties_(properties, 6) : properties);
                if (html) {
                    responses.push({
                        response: response,
                        feature: features[i],
                        layer: layer,
                        title: layer.title,
                        html: html
                    });
                }
            }
        }
        return responses;
    }

    /**
     * fetch a file based template via url. Check if the url starts with this.templateUrlPrefix first.
     * 
     * @param {string} url 
     * @returns {string} template
     */
    static fetchTemplate(url) {
        return new Promise((resolve, reject) => {
            if (url in this.urlCache) {
                resolve(this.urlCache[url]);
            } else if (url.startsWith(this.templateUrlPrefix)) {
                const xhttp = new XMLHttpRequest();
                xhttp.onreadystatechange = e => {
                    if (xhttp.readyState == 4) {
                        if (xhttp.status == 200) {
                            PopupRenderer.urlCache[url] = xhttp.responseText;
                            resolve(xhttp.responseText);
                        } else {
                            reject(`could not load template: ${url}, code: ${xhttp.status}`)
                        }

                    }
                };
                xhttp.onerror = e => {
                    reject(e);
                }
                xhttp.open("GET", url, true);
                xhttp.send();
            } else {
                reject(`Invalid url prefix: ${this.templateUrlPrefix}`);
            }
        });
    }

    /**
     * Get the template for a layer without resolving it
     * 
     * @param {Layer} layer 
     * @param {string} style popup or sidebar
     * @returns {string} template
     */
    static async getTemplate(layer, style) {
        const resolveRegisteredTemplate = function (template) {
            if ((typeof template == "function") || (typeof template == "string")) {
                return template;
            } else if (typeof template == "object") {
                if (!style || !(style in template)) style = "popup";
                if ((style in template) && ((typeof template[style] == "function") || (typeof template[style] == "string"))) {
                    return template[style];
                }
            }
            return null;
        };

        const resolveString = async function (template) {
            if (template.startsWith('#')) {
                if (template.substr(1) in PopupRenderer.templates) {
                    console.log('Using named popup template', template, 'for', layer.uniqueId);
                    return resolveRegisteredTemplate(PopupRenderer.templates[template.substr(1)]);
                }
            } else if (template.length > 0) {
                console.log('Using file based template', template, 'for', layer.uniqueId);
                try {
                    return await PopupRenderer.fetchTemplate(template);
                }
                catch (error) {
                    console.warn(error);
                    return null
                }
            }
            return null;
        }

        if (layer.metadataTemplate) {
            const template = layer.metadataTemplate;
            if (typeof template == "string") {
                return await resolveString(template);
            } else if (typeof template == "object") {
                if (!style || !(style in template)) style = "popup";
                if ((style in template) && (typeof template[style] == "string")) {
                    return await resolveString(template[style]);
                }
            }
        } else {
            // try to find uId matching template
            for (let regex in PopupRenderer.templatesFiltered) {
                if (layer.uniqueId.match(new RegExp(regex))) {
                    console.log('Using regex popup template', regex, 'for', layer.uniqueId);
                    return resolveRegisteredTemplate(PopupRenderer.templatesFiltered[regex]);
                }
            }
        }

        return null;
    }

    static renderTemplate(featureCollection, response, layer, style, customFunctions, template) {
        if (typeof template === 'string') {
            // interpret strings as markdown and resolve template syntax

            const functions = (customFunctions) ? Object.assign({}, templateFunctions, customFunctions) : templateFunctions;
            const responses = [];
            for (let i = 0; i < featureCollection.features.length; ++i) {
                const feature = featureCollection.features[i];
                const data = Object.assign({ layer: layer, response: response }, feature)
                try {
                    const html = resolveComplexTemplate(template, data, functions, {
                        markdown: true,
                        postProcess: true
                    });
                    enhanceHTML(html, (style == "sidebar") ? {} : { groupedHeadlines: false })

                    responses.push({
                        response: response,
                        feature: feature,
                        layer: layer,
                        title: html.dataset.title || layer.title,
                        html: html
                    });
                } catch (msg) {
                    if (msg != "discard_template") console.warn(msg);
                }
            }
            return responses;
        } else if (typeof template === 'function') {
            // resolve function based templates
            const responses = template(featureCollection, response, layer, style);
            if (responses) return responses;
        }

        return [];
    }

    /**
     * reduce size of the feature properties
     * and return it as a new object
     * 
     * @param {object} properties 
     * @param {number} count 
     */
    static reduceProperties_(properties, count) {
        const out = {};

        for (let key in properties) {
            if (properties[key] !== null) {
                if (count-- > 0) {
                    out[key] = properties[key];
                } else {
                    break;
                }
            }
        }

        return out;
    }

}

// Static initialization (static blocks in classes are not supported in every browser)    
for (let i in templates) {
    templates[i](PopupRenderer);
}
